import { useEffect, useState } from "react";

import { Extension } from "@tiptap/core";
import { Fragment, Slice } from "@tiptap/pm/model";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { dropPoint } from "@tiptap/pm/transform";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { ySyncPluginKey } from "@tiptap/y-tiptap";

import { FileCard } from "@/components/tiptap/FileCard";
import { SelectedOverlay } from "@/components/tiptap/SelectedOverlay";
import { createWidgetMap } from "@/components/tiptap/utils/widgetRoot";
import { twMerge } from "@/utils/twMerge";

import type { NodeType, Node as ProsemirrorNode } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

type PlaceholderState = {
  /** Stable widget identity (`spec.key`), so a re-added decoration is `eq` to
   * the one it replaces and ProseMirror reuses the mounted DOM. */
  key: string;
  filename: string;
  contentType: string;
  /** When set, render the file itself (an image) at this size instead of a progress card. */
  preview?: { url: string; width?: number; height?: number };
  setProgress?: (progress: number) => void;
  setSelected?: (selected: boolean) => void;
  cancel: () => void;
  /** Set once a local edit or selection change lands while the upload runs. */
  userMovedOn?: boolean;
};
type PlaceholderSpec = { placeholderState?: PlaceholderState };
type PlaceholderAction = {
  add?: { placeholderState: PlaceholderState; pos: number };
  remove?: { placeholderState: PlaceholderState };
  clickSelect?: { placeholderState: PlaceholderState };
};
type UploadPlaceholderPluginState = {
  decorations: DecorationSet;
  selectedPlaceholder?: PlaceholderState;
};
type PlaceholderOptions = {
  preview?: PlaceholderState["preview"];
  controller: AbortController;
  /** Select the inserted node once the upload finishes. */
  select?: boolean;
};
type UploadPlaceholderOptions = {
  renderPlace?: string;
};

const uploadPlaceholderKey = new PluginKey<UploadPlaceholderPluginState>("uploadPlaceholder");

let nextPlaceholderId = 0;

function findPlaceholder(set: DecorationSet, placeholderState: PlaceholderState) {
  return set.find(
    undefined,
    undefined,
    (spec: PlaceholderSpec) => spec.placeholderState === placeholderState,
  );
}

function UploadPlaceholderWidget({
  placeholderState,
  renderPlace,
  view,
}: {
  placeholderState: PlaceholderState;
  renderPlace?: string;
  view: EditorView;
}) {
  const [progress, setProgress] = useState(0);
  const [selected, setSelected] = useState(false);

  useEffect(() => {
    placeholderState.setProgress = setProgress;
    placeholderState.setSelected = setSelected;

    return () => {
      // Unmount of a replaced widget is deferred and can run after the
      // re-added widget registered its own setters — only clear our own.
      if (placeholderState.setProgress === setProgress) {
        placeholderState.setProgress = undefined;
      }
      if (placeholderState.setSelected === setSelected) {
        placeholderState.setSelected = undefined;
      }
    };
  }, [placeholderState]);

  const { filename, contentType, preview } = placeholderState;
  const percent = Math.round(progress * 100);
  const isChatInput = renderPlace === "chat-input";

  return (
    <div
      className={twMerge(
        "upload-placeholder relative w-full select-none cursor-default",
        !isChatInput && "line-wrapper max-w-content-width mx-auto sm:max-w-full",
      )}
      contentEditable={false}
      data-testid="upload-placeholder"
      data-selected={selected || undefined}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();

        view.focus();
        view.dispatch(
          view.state.tr.setMeta(uploadPlaceholderKey, { clickSelect: { placeholderState } }),
        );
      }}
    >
      <div
        className={twMerge(
          "flex flex-1",
          isChatInput ? "mb-2.5" : "my-2.5",
          preview ? "items-baseline" : "items-center",
        )}
      >
        {!isChatInput && (
          <div className="relative flex items-center select-none fromSm:-ml-7">
            <span className="line-number text-xxs text-right sm:text-left w-line-number-width sm:w-6.25 text-gray-400 dark:text-gray-600 selection:bg-transparent" />
            <div className="shrink-0 w-7 sm:hidden" />
          </div>
        )}
        {preview ? (
          <div
            className="relative max-w-full"
            style={{
              width: preview.width,
              aspectRatio:
                preview.width && preview.height
                  ? `${preview.width} / ${preview.height}`
                  : undefined,
            }}
          >
            {selected && <SelectedOverlay />}
            <div className="w-0 h-0">&nbsp;</div>
            <img src={preview.url} alt="" className="w-full h-full object-contain opacity-80" />
          </div>
        ) : (
          <FileCard filename={filename} contentType={contentType} selected={selected}>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              className="self-center flex-1 min-w-[50%] h-[3px] mx-2 rounded-full bg-gray-200 dark:bg-gray-700"
            >
              <div
                className="h-full rounded-full bg-icon-active transition-[width]"
                style={{ width: `${percent}%` }}
              />
            </div>
          </FileCard>
        )}
      </div>
    </div>
  );
}

/**
 * Maps a position that usually points into a text block to the closest
 * boundary where the block node is actually allowed, so the placeholder and
 * the uploaded card land on their own line.
 */
export function blockInsertionPoint(state: EditorState, position: number, nodeType: NodeType) {
  const clamped = Math.min(position, state.doc.content.size);
  const slice = new Slice(Fragment.from(nodeType.create()), 0, 0);
  return dropPoint(state.doc, clamped, slice) ?? clamped;
}

/**
 * Renders a widget decoration at the pending insertion point of a dropped
 * file while it uploads, driven by `insertWithUploadPlaceholder` via meta
 * transactions. Decorations are local-only (nothing syncs to collaborators)
 * and are remapped through every transaction, so the insertion point follows
 * concurrent edits.
 */
export const UploadPlaceholder = Extension.create<UploadPlaceholderOptions>({
  name: "uploadPlaceholder",
  // Above KeyboardHandler, whose Backspace/Delete keymap would otherwise
  // consume the key before handleKeyDown sees a selected placeholder.
  priority: 1000,
  addOptions() {
    return { renderPlace: undefined };
  },
  addProseMirrorPlugins() {
    const { renderPlace } = this.options;
    const { mountWidgetRoot, unmountWidgetRoot } = createWidgetMap();

    const createDecoration = (pos: number, placeholderState: PlaceholderState) =>
      Decoration.widget(
        pos,
        (view) =>
          mountWidgetRoot(
            <UploadPlaceholderWidget
              placeholderState={placeholderState}
              renderPlace={renderPlace}
              view={view}
            />,
          ),
        { key: placeholderState.key, placeholderState, destroy: unmountWidgetRoot },
      );

    return [
      new Plugin<UploadPlaceholderPluginState>({
        key: uploadPlaceholderKey,
        state: {
          init: () => ({ decorations: DecorationSet.empty }),
          apply(tr, prev) {
            const { decorations, selectedPlaceholder } = prev;
            const { isChangeOrigin, isUndoRedoOperation } =
              (tr.getMeta(ySyncPluginKey) as Record<string, boolean> | undefined) ?? {};
            // Remote edits arrive as replacements of whole node ranges and can
            // drop a placeholder that merely sat inside the replaced range.
            // The placeholder is local-only — collaborators can't target it —
            // so re-add it at its mapped position instead of cancelling.
            const isRemote = isChangeOrigin && !isUndoRedoOperation;
            const before = isRemote ? decorations.find() : [];
            let mapped = decorations.map(tr.mapping, tr.doc, {
              onRemove: (spec: PlaceholderSpec) => {
                if (!isRemote) spec.placeholderState?.cancel();
              },
            });
            for (const decoration of before) {
              const { placeholderState } = decoration.spec as PlaceholderSpec;
              if (!placeholderState || findPlaceholder(mapped, placeholderState).length > 0) {
                continue;
              }
              const pos = Math.min(tr.mapping.map(decoration.from), tr.doc.content.size);
              mapped = mapped.add(tr.doc, [createDecoration(pos, placeholderState)]);
            }
            if (!isChangeOrigin && (tr.docChanged || tr.selectionSet)) {
              for (const decoration of mapped.find()) {
                const { placeholderState } = decoration.spec as PlaceholderSpec;
                if (placeholderState) placeholderState.userMovedOn = true;
              }
            }

            const action = tr.getMeta(uploadPlaceholderKey) as PlaceholderAction | undefined;

            if (action?.add) {
              const { placeholderState, pos } = action.add;
              mapped = mapped.add(tr.doc, [createDecoration(pos, placeholderState)]);
            }

            if (action?.remove) {
              mapped = mapped.remove(findPlaceholder(mapped, action.remove.placeholderState));
            }

            let nextSelectedPlaceholder =
              !isChangeOrigin && (tr.docChanged || tr.selectionSet)
                ? undefined
                : selectedPlaceholder;
            if (action?.clickSelect) {
              nextSelectedPlaceholder = action.clickSelect.placeholderState;
            }
            if (
              nextSelectedPlaceholder &&
              findPlaceholder(mapped, nextSelectedPlaceholder).length === 0
            ) {
              nextSelectedPlaceholder = undefined;
            }

            if (mapped === decorations && nextSelectedPlaceholder === selectedPlaceholder) {
              return prev;
            }

            return { decorations: mapped, selectedPlaceholder: nextSelectedPlaceholder };
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)?.decorations;
          },
          handleKeyDown(view, event) {
            if (event.key !== "Backspace" && event.key !== "Delete") return false;

            const selectedPlaceholder = uploadPlaceholderKey.getState(
              view.state,
            )?.selectedPlaceholder;
            if (!selectedPlaceholder) return false;

            view.dispatch(
              view.state.tr.setMeta(uploadPlaceholderKey, {
                remove: { placeholderState: selectedPlaceholder },
              }),
            );
            selectedPlaceholder.cancel();
            return true;
          },
        },
        view: (editorView) => ({
          update: (view, prevState) => {
            const pluginState = uploadPlaceholderKey.getState(view.state);
            if (!pluginState || pluginState.decorations === DecorationSet.empty) return;

            if (
              pluginState === uploadPlaceholderKey.getState(prevState) &&
              view.state.selection === prevState.selection
            ) {
              return;
            }

            const { from, to } = view.state.selection;
            pluginState.decorations.find().forEach((decoration) => {
              const { placeholderState } = decoration.spec as PlaceholderSpec;
              placeholderState?.setSelected?.(
                (from < decoration.from && decoration.from < to) ||
                  pluginState.selectedPlaceholder === placeholderState,
              );
            });
          },
          destroy: () => {
            uploadPlaceholderKey
              .getState(editorView.state)
              ?.decorations.find()
              .forEach((decoration) => {
                (decoration.spec as PlaceholderSpec).placeholderState?.cancel();
              });
          },
        }),
      }),
    ];
  },
});

/**
 * Runs `upload` while showing a placeholder at `position` (mapped to the
 * nearest boundary where `nodeType` is allowed), then inserts the node built
 * by `createNode` at the placeholder's current (remapped) position. Skips
 * insertion when the upload returns null/rejects or the placeholder's
 * position was deleted by a concurrent edit. Pass `preview` to render
 * the file itself (an image) at a fixed size instead of the progress card,
 * and `select` to select the inserted node unless the user has edited or moved
 * the selection since the upload started.
 */
export async function insertWithUploadPlaceholder<Attrs>(
  view: EditorView,
  nodeType: NodeType,
  position: number,
  file: File,
  upload: (onProgress: (progress: number) => void) => Promise<Attrs | null>,
  createNode: (attrs: Attrs) => ProsemirrorNode,
  { preview, controller, select }: PlaceholderOptions,
): Promise<boolean> {
  const placeholderState: PlaceholderState = {
    key: `upload-placeholder-${nextPlaceholderId++}`,
    filename: file.name,
    contentType: file.type,
    preview,
    cancel: () => controller.abort(),
  };
  const pos = blockInsertionPoint(view.state, position, nodeType);
  view.dispatch(view.state.tr.setMeta(uploadPlaceholderKey, { add: { placeholderState, pos } }));

  let attrs: Attrs | null = null;
  try {
    attrs = await upload((progress) => placeholderState.setProgress?.(progress));
  } catch (error) {
    if (!controller.signal.aborted) {
      console.error("Failed to upload file", error);
    }
  }

  if (view.isDestroyed) return false;

  const decorations = uploadPlaceholderKey.getState(view.state)?.decorations;
  const placeholder = decorations ? findPlaceholder(decorations, placeholderState)[0] : undefined;
  if (!placeholder) return false;

  try {
    const transaction = view.state.tr.setMeta(uploadPlaceholderKey, {
      remove: { placeholderState },
    });
    if (attrs !== null) {
      transaction.insert(placeholder.from, createNode(attrs));
      // Don't pull the selection back if the user moved on (typed, tapped
      // elsewhere) while the upload ran — their next keystroke would replace
      // the image.
      if (select && !placeholderState.userMovedOn) {
        transaction.setSelection(NodeSelection.create(transaction.doc, placeholder.from));
      }
    }
    view.dispatch(transaction);
    return attrs !== null;
  } catch (error) {
    console.error("Failed to insert uploaded file", error);
    return false;
  }
}
